import type { KyInstance } from 'ky';

import { DEFAULT_DATE_FORMAT, ensureError, formatDate, ifDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { GetMailingCalendarQuery } from './types/get-mailing-calendar-query.type';
import type { MailingCalendar } from './types/mailing-calendar.type';

/**
 * Fetches the mailing calendar data from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters for fetching the mailing calendar, including start and end dates,
 * and search string.
 * @param isExternal - Optional flag to determine if the external API endpoint should be used. Defaults to `false`.
 * @returns A promise that resolves to an `OperationResultWithData<MailingCalendar>`.
 * @throws Throws an error if the API request fails.
 */
export const getMailingCalendar = async (instance: KyInstance, query: GetMailingCalendarQuery, isExternal = false) => {
  const { endDate, search, startDate } = query;

  try {
    return await instance
      .get<OperationResultWithData<MailingCalendar>>(
        isExternal ? Uri.calendar.getMailingCalendarExternal : Uri.calendar.getMailingCalendar,
        {
          searchParams: {
            endDate: ifDefined(endDate, (date) => {
              return formatDate(date, DEFAULT_DATE_FORMAT);
            }),
            search,
            startDate: ifDefined(startDate, (date) => {
              return formatDate(date, DEFAULT_DATE_FORMAT);
            }),
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
